import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { RECEPTION_DEPARTURE_REASONS } from '../utils/reception';
import type { ReceptionDepartureReason, ReceptionVisit } from '../utils/reception';

export default function LeaveReceptionDialog({
  visit,
  onClose,
  onSave,
}: {
  visit: ReceptionVisit | null;
  onClose: () => void;
  onSave: (visit: ReceptionVisit, reason: ReceptionDepartureReason, note: string) => Promise<void>;
}) {
  const [reason, setReason] = React.useState<ReceptionDepartureReason | null>(null);
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (visit) { setReason(null); setNote(''); setError(''); }
  }, [visit?.id]);

  const save = async () => {
    if (!visit || !reason || (reason === 'other' && !note.trim()) || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave(visit, reason, note);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not record the departure. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!visit} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="leave-reception-title">
      <DialogTitle id="leave-reception-title" sx={{ fontWeight: 750 }}>Visitor left reception</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography sx={{ fontSize: 14, color: '#5b5b5b' }}>
          {visit?.name} left without a presenter meeting. What happened?
        </Typography>
        <Box role="group" aria-label="Reason for leaving" sx={{ display: 'grid', gap: 1 }}>
          {RECEPTION_DEPARTURE_REASONS.map((item) => (
            <Box key={item.key} component="button" type="button" aria-pressed={reason === item.key}
              onClick={() => setReason(item.key)}
              sx={{ minHeight: 44, px: 1.5, borderRadius: 1.5, textAlign: 'left', cursor: 'pointer',
                border: `1px solid ${reason === item.key ? '#9d6f32' : '#e8e3da'}`,
                bgcolor: reason === item.key ? '#faf4eb' : '#fff', fontSize: 14, fontWeight: reason === item.key ? 700 : 500 }}>
              {item.label}
            </Box>
          ))}
        </Box>
        <TextField label={reason === 'other' ? 'What happened? *' : 'Note (optional)'}
          value={note} onChange={(event) => setNote(event.target.value)} multiline minRows={2}
          inputProps={{ maxLength: 500 }} helperText={`${note.length}/500 characters`} />
        {error && <Typography role="alert" sx={{ fontSize: 13, color: '#b3261e' }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: '#5b5b5b', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={save} disabled={!reason || (reason === 'other' && !note.trim()) || saving}
          variant="contained" disableElevation sx={{ bgcolor: '#1c1b19', textTransform: 'none', '&:hover': { bgcolor: '#33312d' } }}>
          {saving ? 'Saving…' : 'Record departure'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
